// components/layout/Layout.js
import React, { useState, useRef, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useTranslations } from '../../hooks/useTranslations';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import CustomerActionsNotifier from '../dashboard/CustomerActionsNotifier';

export function Layout({
  children,
  currentPage,
  onNavigate,
  headerTitle,
  headerSubtitle,
  headerActions = [],
  breadcrumbs = [],
  sidebarCollapsed = false,
  centerHeaderComponent = null
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);

  const { config, logoUrl, logoLoading } = useBusinessConfig();

  const quickActionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setIsQuickActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden">
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={toggleMobileMenu}
          />
        )}

        <div className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
          <Sidebar
            currentPage={currentPage}
            onNavigate={(page) => {
              onNavigate(page);
              setIsMobileMenuOpen(false);
            }}
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex lg:flex-shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        }`}>
        <Sidebar
          currentPage={currentPage}
          onNavigate={onNavigate}
          collapsed={sidebarCollapsed}
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile header with menu button */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Center component */}
            {centerHeaderComponent && (
              <div className="flex-1 flex items-center justify-center mx-4">
                {centerHeaderComponent}
              </div>
            )}

            <div className="flex items-center space-x-3">
              <div className="flex items-center px-2">
                {logoLoading ? (
                  <div className="h-8 lg:h-12 w-8 lg:w-12 bg-gray-200 animate-pulse rounded" />
                ) : (
                  <div className="h-8 lg:h-12 w-auto"></div>
                  // <img
                  //   src={logoUrl}
                  //   alt="Company Logo"
                  //   className="h-8 lg:h-12 w-auto"
                  //   onError={(e) => {
                  //     e.target.src = '/clinic-q-logo.png';
                  //   }}
                  // />
                )}
              </div>

              {/* Quick Actions Dropdown - Only show if there are actions */}
              {headerActions.length > 0 && (
                <div className="relative" ref={quickActionsRef}>
                  <button
                    onClick={() => setIsQuickActionsOpen(!isQuickActionsOpen)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 transition-colors"
                    aria-label="Quick Actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isQuickActionsOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      {headerActions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            action.onClick();
                            setIsQuickActionsOpen(false);
                          }}
                          className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {action.icon && (
                            <span className="ml-3 text-gray-500">
                              {action.icon}
                            </span>
                          )}
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {config?.settings?.approval_workflow_enabled && (
                <CustomerActionsNotifier />
              )}
            </div>
          </div>
        </div>

        {/* Desktop header */}
        {(headerTitle || headerActions.length > 0 || breadcrumbs.length > 0) && (
          <Header
            title={headerTitle}
            subtitle={headerSubtitle}
            actions={headerActions}
            breadcrumbs={breadcrumbs}
            centerComponent={centerHeaderComponent}
            className="hidden lg:block"
          />
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

// Specialized layout for dashboard
export function DashboardLayout({ children, currentPage, onNavigate }) {
  const t = useTranslations();
  const { business_name } = useBusinessConfig();

  return (
    <Layout
      currentPage={currentPage}
      onNavigate={onNavigate}
      headerTitle={`${t.welcomeTitle} - ${business_name}`}
      headerSubtitle={t.welcomeSubtitle}
    >
      {children}
    </Layout>
  );
}

// Specialized layout for content pages
export function ContentLayout({
  children,
  currentPage,
  onNavigate,
  title,
  description,
  primaryAction,
  secondaryActions = [],
  centerHeaderComponent = null
}) {
  const headerActions = [];
  const t = useTranslations();

  if (secondaryActions.length > 0) {
    headerActions.push(...secondaryActions);
  }

  if (primaryAction) {
    headerActions.push(primaryAction);
  }

  return (
    <Layout
      currentPage={currentPage}
      onNavigate={onNavigate}
      headerTitle={title}
      headerSubtitle={description}
      headerActions={headerActions}
      centerHeaderComponent={centerHeaderComponent}
    >
      {children}
    </Layout>
  );
}

// Page container for consistent padding and spacing
export function PageContainer({
  children,
  className = '',
  mx = 'mx-auto',
  px = 'px-4 sm:px-6 lg:px-8',
  py = 'lg:py-6 sm:py-0'
}) {
  return (
    <div className={`max-w-7xl ${mx} ${px} ${py} ${className}`} style={{ backgroundColor: '#F7F1F1' }}>
      {children}
    </div>
  );
}

// Content section with consistent spacing
export function ContentSection({
  title,
  description,
  children,
  className = '',
  headerActions = []
}) {
  return (
    <section className={`mb-8 ${className}`}>
      {(title || description || headerActions.length > 0) && (
        <div className="flex items-center justify-between mb-6">
          <div>
            {title && (
              <h2 className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-sm text-gray-600 mt-1">
                {description}
              </p>
            )}
          </div>

          {headerActions.length > 0 && (
            <div className="flex items-center space-x-2 mr-2">
              {headerActions}
            </div>
          )}
        </div>
      )}

      {children}
    </section>
  );
}
